'use client';

import { c as _c } from "react/compiler-runtime";
import { useToast } from '@common/hooks';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { AuthInfiniteQueryOptions, AuthMutation } from '@/api';
import { ReviewCard } from '@/app/donation/_components/ReviewCard';
import { ReviewCardMenu } from '@/app/donation/_components/ReviewCardMenu';
import { MarketReviewDetailContent, type MarketReviewItem } from '@/app/welmarket/_components/MarketReviewDetailContent';
import { openResponsiveDialog } from '@/stores/DrawerStore';
import { LocalMarketReviewEditModal } from './LocalMarketReviewEditModal';
const PAGE_SIZE = 20;
export const LocalMarketReviewList = () => {
  const $ = _c(28);
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const [editTargetId, setEditTargetId] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      ...AuthInfiniteQueryOptions["get:/wello/v2/auth/local-market/my/reviews"]({
        query: {
          page: 1,
          size: PAGE_SIZE
        }
      }),
      getNextPageParam: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(t0);
  const observerRef = useRef(null);
  let t1;
  let t2;
  if ($[1] !== fetchNextPage || $[2] !== hasNextPage || $[3] !== isFetchingNextPage) {
    t1 = () => {
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }, {
        rootMargin: "0px 0px 1000px 0px"
      });
      if (observerRef.current) {
        observer.observe(observerRef.current);
      }
      return () => observer.disconnect();
    };
    t2 = [fetchNextPage, hasNextPage, isFetchingNextPage];
    $[1] = fetchNextPage;
    $[2] = hasNextPage;
    $[3] = isFetchingNextPage;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  const totalCount = data?.pages[0]?.context?.pageInfo?.totalCount ?? 0;
  const reviews_0 = data?.pages.flatMap(_temp2) ?? [];
  const extractImageUrls = _temp5;
  const mapToDetailReviewItems = () => reviews_0.map(review_0 => ({
    rating: review_0.rating ?? 0,
    nickname: review_0.nickname ?? "",
    date: dayjs(review_0.createdAt).format("YYYY.MM.DD"),
    content: review_0.contents ?? "",
    imageUrlList: extractImageUrls(review_0),
    productName: review_0.productName ?? ""
  }));
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = AuthMutation["put:/wello/v2/auth/local-market/my/reviews/{reviewId}"]();
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== queryClient || $[8] !== toast) {
    t4 = async () => {
      await queryClient.invalidateQueries();
      toast({
        message: "\uD6C4\uAE30\uB97C \uC218\uC815\uD588\uC5B4\uC694."
      });
      setEditTargetId(null);
    };
    $[7] = queryClient;
    $[8] = toast;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== toast) {
    t5 = () => {
      toast({
        type: "fail",
        message: "\uD6C4\uAE30 \uC218\uC815\uC5D0 \uC2E4\uD328\uD588\uC5B4\uC694."
      });
    };
    $[10] = toast;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = {
      ...t3,
      onSuccess: t4,
      onError: t5
    };
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const {
    mutate: updateReview,
    isPending: isUpdating
  } = useMutation(t6);
  let t7;
  if ($[15] !== updateReview) {
    t7 = (reviewId, data_0) => {
      updateReview({
        path: {
          reviewId
        },
        body: data_0
      });
    };
    $[15] = updateReview;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleEditSubmit = t7;
  let t8;
  if ($[17] !== totalCount) {
    t8 = <p className="px-20 py-8 font-500 text-Gray-black typo-body4">총 {totalCount}개</p>;
    $[17] = totalCount;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = totalCount === 0 ? <p className="py-80 text-center font-400 text-Gray-550 typo-body3">등록된 후기가 없어요.</p> : <><div>{reviews_0.map((item, index) => <div key={item.reviewId} role="button" tabIndex={0} className="w-full cursor-pointer text-left" onClick={() => openResponsiveDialog({
        component: MarketReviewDetailContent,
        modalHeader: "\uD6C4\uAE30 \uC0C1\uC138",
        className: {
          modal: "max-w-[900px]"
        },
        props: {
          reviews: mapToDetailReviewItems(),
          initialIndex: index
        },
        drawerNoPadding: true
      })}><ReviewCard rating={item.rating ?? 0} createdAt={item.createdAt ?? ""} productName={item.productName ?? ""} contents={item.contents ?? ""} imageUrlList={extractImageUrls(item)} reviewer={item.nickname ?? ""} answer={item.answer} answerAt={item.answerAt} actions={item.reviewId ? <ReviewCardMenu onEdit={() => setEditTargetId(item.reviewId)} /> : undefined} /></div>)}</div>{hasNextPage && <div ref={observerRef} className="h-4 w-full" />}</>;
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => setEditTargetId(null);
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== editTargetId || $[21] !== handleEditSubmit || $[22] !== isUpdating) {
    t11 = <LocalMarketReviewEditModal reviewId={editTargetId} onClose={t10} onSubmit={handleEditSubmit} isSubmitting={isUpdating} />;
    $[20] = editTargetId;
    $[21] = handleEditSubmit;
    $[22] = isUpdating;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t8 || $[26] !== t9) {
    t12 = <div>{t8}{t9}{t11}</div>;
    $[24] = t11;
    $[25] = t8;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
};
function _temp(lastPage, allPages) {
  const reviews = lastPage.context?.reviews ?? [];
  if (reviews.length < PAGE_SIZE) {
    return;
  }
  return allPages.length + 1;
}
function _temp2(page) {
  return page.context?.reviews ?? [];
}
function _temp3(file) {
  return file.type === "IMAGE";
}
function _temp4(file_0) {
  return file_0.url ?? "";
}
function _temp5(review) {
  return review.fileUrls?.filter(_temp3).map(_temp4) ?? [];
}